import {
  ImageLink,
  NavItemLevelOneProps,
  NavItemLevelTwoProps,
  SiteHeaderProps,
} from '@stadionhq-v3/base-ui';
import { ContentfulImage, ContentfulLogo } from '@stadionhq-v3/contentful-ui';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { SiteSettings } from '@graphql/__generated__/graphql';
import { Locale } from '@core/constants/locales.constants';
import templateConfig from '@templateConfig';
import { Translations } from '@lib/dictionary-keys/__generated__';
import {
  ArrowLeft,
  ArrowRight,
  Bento,
  Close,
  Menu,
  Plus,
} from '@components/icons';
import PromoCard from '@components/v3/molecules/promo-card';
import { assetTransform, linkTransform } from '../atoms';

const siteHeaderTransform = (
  {
    footer,
    headerPrimaryNavigation,
    headerMoreNavigation,
  }: CleanContentful<SiteSettings>,
  locale: Locale,
  dictionaryKeys: {
    back: Translations['navigation']['action']['backButton'];
    more: Translations['navigation']['action']['more'];
    close: Translations['common']['action']['closeButton'];
  }
): Omit<
  SiteHeaderProps,
  | 'pointerType'
  | 'setPointerType'
  | 'isKeyboardFocus'
  | 'setIsKeyboardFocus'
  | 'overlayIsActive'
  | 'setOverlayIsActive'
  | 'mainNavIsActive'
  | 'setMainNavIsActive'
> => ({
  homePageLogo: {
    image: footer?.logo?.url && (
      <ContentfulLogo {...assetTransform(footer.logo)} width={72} height={72} />
    ),
    link: {
      url: templateConfig.navigation.routes.HOME,
      title: 'Homepage', // TODO: add dictionary key
    },
  },
  navItems: headerPrimaryNavigation?.navigationItems
    ?.filter(Boolean)
    ?.slice(0, 7)
    ?.filter(
      (primaryNavLink) =>
        primaryNavLink?.primaryLink?.url ||
        primaryNavLink.primaryLink?.internalLinkData
    )
    .map((firstLevelNavItem) => ({
      ...linkTransform(
        { ...firstLevelNavItem?.primaryLink },
        locale,
        <ArrowRight />
      ),
      secondaryLinks: firstLevelNavItem?.secondaryLinks
        ?.filter(
          (secondLevelNavItem) =>
            secondLevelNavItem?.primaryLink?.url ||
            secondLevelNavItem.primaryLink?.internalLinkData
        )
        ?.map((secondLevelNavItem) => ({
          ...linkTransform(
            { ...secondLevelNavItem?.primaryLink },
            locale,
            <ArrowRight />
          ),
          secondaryLinks: secondLevelNavItem?.secondaryLinks
            ?.filter(
              (thirdLevelNavItem) =>
                thirdLevelNavItem?.primaryLink?.url ||
                thirdLevelNavItem.primaryLink?.internalLinkData
            )
            ?.map((thirdLevelNavItem) => ({
              ...linkTransform({ ...thirdLevelNavItem?.primaryLink }, locale),
            })),
        })),
      promoBlock: firstLevelNavItem?.promoBlock?.image &&
        firstLevelNavItem?.promoBlock?.link?.__typename === 'Link' &&
        firstLevelNavItem?.promoBlock?.link?.url && (
          <PromoCard
            image={<ContentfulImage {...firstLevelNavItem.promoBlock.image} />}
            link={{
              ...linkTransform(firstLevelNavItem.promoBlock.link, locale),
            }}
            title={firstLevelNavItem.promoBlock.promoTitle}
          />
        ),
    })) as NavItemLevelOneProps[],
  moreMenuProps: {
    // Section titles render as navigable links (NavItemLevelFour) so each
    // section's own internal link works — see siteHeader.component.tsx.
    sectionTitleType: 'link',
  },
  moreMenuItems: headerMoreNavigation?.navigationItems
    ?.filter(Boolean)
    ?.filter(
      (secondLevelNavItem) =>
        secondLevelNavItem?.primaryLink?.url ||
        secondLevelNavItem.primaryLink?.internalLink?.url
    )
    .map((secondLevelNavItem) => ({
      ...linkTransform(
        { ...secondLevelNavItem?.primaryLink },
        locale,
        <ArrowRight />
      ),
      nativeProps: {},
      promoBlock: secondLevelNavItem?.promoBlock?.image &&
        secondLevelNavItem?.promoBlock?.link?.__typename === 'Link' &&
        secondLevelNavItem?.promoBlock?.link?.url && (
          <ImageLink
            image={<ContentfulImage {...secondLevelNavItem.promoBlock.image} />}
            link={{
              ...linkTransform(secondLevelNavItem.promoBlock.link, locale),
            }}
          />
        ),
      secondaryLinks: secondLevelNavItem?.secondaryLinks
        ?.filter(Boolean)
        ?.filter(
          (thirdLevelNavItem) =>
            thirdLevelNavItem?.primaryLink?.url ||
            thirdLevelNavItem.primaryLink?.internalLink?.url
        )
        .map((thirdLevelNavItem) => ({
          ...linkTransform({ ...thirdLevelNavItem?.primaryLink }, locale),
          nativeProps: {},
        })),
    })) as NavItemLevelTwoProps[],
  searchOverlayTrigger: undefined,
  megaMenuTrigger: {
    text: 'burger menu',
    iconAfter: <Menu />,
    size: 'large',
    iconOnly: true,
    isNeutral: true,
    variant: 'primary',
  },
  megaMenuBackButton: {
    text: dictionaryKeys.back,
    iconBefore: <ArrowLeft />,
    size: 'small',
    variant: 'primary',
    isNeutral: true,
  },
  moreMenuTrigger: {
    triggerIcon: <Bento />,
    triggerLabel: `${dictionaryKeys.more}...`,
    closeIcon: <Close />,
    closeLabel: '',
    iconAfter: <Plus />,
    isSelected: false,
    disabled: false,
    desktopType: 'navItem',
    mobileType: 'navItem',
  },
});

export default siteHeaderTransform;
